"use client";

import { m, useReducedMotion } from "motion/react";
import Image from "next/image";

import { mergeClasses } from "@trex-2b/libs";

/**
 * Partner logos — Figma StandardDesign `9223:3991` (row `9223:3992`, slots `9223:3993`…`9223:3999`).
 * Static SVGs mirror each child slot in document order (`logo-1` … `logo-7`).
 */
const PARTNER_LOGOS = [
  {
    nodeId: "9223:3993",
    src: "/images/landing/logos/logo-1.svg",
    width: 147,
    height: 40,
    alt: "Arbitrum",
  },
  {
    nodeId: "9223:3994",
    src: "/images/landing/logos/logo-2.svg",
    width: 142,
    height: 40,
    alt: "AltLayer",
  },
  {
    nodeId: "9223:3995",
    src: "/images/landing/logos/logo-3.svg",
    width: 135,
    height: 40,
    alt: "Dune",
  },
  {
    nodeId: "9223:3996",
    src: "/images/landing/logos/logo-4.svg",
    width: 129,
    height: 40,
    alt: "Hyperliquid",
  },
  {
    nodeId: "9223:3997",
    src: "/images/landing/logos/logo-5.svg",
    width: 123,
    height: 40,
    alt: "thirdweb",
  },
  {
    nodeId: "9223:3998",
    src: "/images/landing/logos/logo-6.svg",
    width: 121,
    height: 40,
    alt: "Alchemy",
  },
  {
    nodeId: "9223:3999",
    src: "/images/landing/logos/logo-7.svg",
    width: 142,
    height: 40,
    alt: "Moralis",
  },
] as const;

function PartnerLogoStrip({
  ariaHidden,
  duplicate,
}: {
  ariaHidden?: boolean;
  duplicate?: boolean;
}) {
  return (
    <div
      aria-hidden={ariaHidden}
      className="flex shrink-0 items-center gap-14 pr-14"
      data-node-id={duplicate ? undefined : "9223:3992"}
    >
      {PARTNER_LOGOS.map((logo) => (
        <div
          key={duplicate ? `${logo.src}-dup` : logo.src}
          className="relative flex h-10 shrink-0 items-center justify-center"
          data-node-id={duplicate ? undefined : logo.nodeId}
        >
          <Image
            alt={ariaHidden ? "" : logo.alt}
            src={logo.src}
            width={logo.width}
            height={logo.height}
            className="h-10 w-auto max-w-none object-contain"
            sizes="(max-width: 1280px) 140px, 160px"
          />
        </div>
      ))}
    </div>
  );
}

export function LandingPartnerLogos() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <m.section
      aria-label="Partner logos"
      className="w-full bg-white"
      data-node-id="9223:3991"
      initial={
        shouldReduceMotion
          ? undefined
          : { opacity: 0, y: 24, filter: "blur(8px)" }
      }
      whileInView={
        shouldReduceMotion
          ? undefined
          : { opacity: 1, y: 0, filter: "blur(0px)" }
      }
      viewport={{ amount: 0.6, once: true }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
    >
      <div
        className={mergeClasses(
          "mx-auto w-full px-[42px] py-12 md:py-14",
          "max-md:px-6",
        )}
      >
        {/* Seamless loop: three strips + translate -33.333% to avoid wide-screen gap flash */}
        <div
          className={mergeClasses(
            "motion-safe:block motion-reduce:hidden",
            "w-full overflow-hidden",
          )}
        >
          <div
            className={mergeClasses(
              "flex w-max animate-landing-partner-marquee",
            )}
          >
            <PartnerLogoStrip />
            <PartnerLogoStrip ariaHidden duplicate />
            <PartnerLogoStrip ariaHidden duplicate />
          </div>
        </div>

        <div
          className={mergeClasses(
            "motion-reduce:flex motion-safe:hidden",
            "w-full flex-wrap items-center justify-center gap-x-8 gap-y-8",
          )}
          data-node-id="9223:3992"
        >
          {PARTNER_LOGOS.map((logo) => (
            <div
              key={logo.src}
              className="relative flex h-10 shrink-0 items-center justify-center"
              data-node-id={logo.nodeId}
            >
              <Image
                alt={logo.alt}
                src={logo.src}
                width={logo.width}
                height={logo.height}
                className="h-10 w-auto max-w-none object-contain"
                sizes="(max-width: 1280px) 140px, 160px"
              />
            </div>
          ))}
        </div>
      </div>
    </m.section>
  );
}
